/**
 * Chamadas de autenticação. Tudo passa pelo `lib/api.ts`, que já desempacota o envelope `{ data }`,
 * põe o Bearer da memória, manda o CSRF em `refresh`/`logout` e faz o refresh silencioso em 401.
 *
 * 🚨 Nenhuma função daqui grava token: quem guarda é o `AuthProvider`, sempre em memória.
 */
import { api } from '@/lib/api';
import type { SessaoApi, Usuario } from '@/tipos/api';
import type {
  DadosAceitarConvite,
  DadosLogin,
  DadosPedirRedefinicao,
  DadosPerfil,
  DadosRedefinirSenha,
  DadosTrocarSenha,
} from './schemas';

/** Para quem é um convite. 🚨 Sem o token: ele está na URL e não precisa voltar na resposta. */
export interface ConvitePublico {
  nome: string;
  email: string;
  /** Nome do papel — rótulo, para a pessoa saber a que está sendo convidada. */
  papel: string;
}

/** `POST /auth/login`. Devolve o access token e o usuário já com as permissões resolvidas. */
export async function login(dados: DadosLogin): Promise<SessaoApi> {
  const { data } = await api.post<SessaoApi>('/auth/login', dados);
  return data;
}

/**
 * `GET /auth/sessao` — quem é e o que pode, agora. O access token **não** vem daqui: ele vem do
 * login ou do refresh. Reler isto é o que faz uma permissão recém-concedida chegar ao menu.
 */
export async function obterSessao(): Promise<{ usuario: Usuario }> {
  const { data } = await api.get<{ usuario: Usuario }>('/auth/sessao');
  return data;
}

/** `POST /auth/logout` — revoga a família do refresh no servidor e limpa os cookies. 204 sem corpo. */
export async function logout(): Promise<void> {
  await api.post('/auth/logout');
}

/** `PATCH /perfil` — só o nome. E-mail e papel não existem no DTO do servidor (regras 6 e 11). */
export async function atualizarPerfil(dados: DadosPerfil): Promise<Usuario> {
  const { data } = await api.patch<Usuario>('/perfil', dados);
  return data;
}

/**
 * `POST /perfil/senha` — exige a senha atual e derruba as **outras** sessões (regra 22).
 *
 * A `confirmacao` fica no formulário e **não** vai no corpo: o servidor tem `forbidNonWhitelisted`, e
 * um campo a mais reprovaria com 422.
 */
export async function trocarSenha(dados: DadosTrocarSenha): Promise<{ sessoesEncerradas: number }> {
  const { data } = await api.post<{ sessoesEncerradas: number }>('/perfil/senha', {
    senhaAtual: dados.senhaAtual,
    senhaNova: dados.senhaNova,
  });
  return data;
}

/**
 * `GET /auth/convites/:token` — para quem é este convite, antes de pedir a senha.
 *
 * **Pública** (o convidado ainda não tem conta ativa) e com uma resposta só para as quatro formas de
 * invalidez: inexistente, vencido, já usado e cancelado. A tela **não deve** tentar distinguir — a
 * mensagem genérica é a defesa, e reescrevê-la com "parece que expirou" a desfaria.
 */
export async function verConvite(token: string): Promise<ConvitePublico> {
  const { data } = await api.get<ConvitePublico>(`/auth/convites/${token}`);
  return data;
}

/**
 * `POST /auth/convites/:token/aceitar` — define a senha, ativa a conta e **já devolve a sessão**.
 *
 * 🚨 O corpo leva **só a senha**: o e-mail sai do convite, no servidor (regra 10). Mandar `email` aqui
 * reprovaria com 422 (`forbidNonWhitelisted`) — e é essa recusa que impede alguém com um link válido
 * de escolher para qual conta a senha vai.
 *
 * A `confirmacao` do formulário também não entra: ela existe só na tela.
 */
export async function aceitarConvite(
  token: string,
  dados: DadosAceitarConvite,
): Promise<SessaoApi> {
  const { data } = await api.post<SessaoApi>(`/auth/convites/${token}/aceitar`, {
    senha: dados.senha,
  });
  return data;
}

// ---------------------------------------------------------------------------
// Esqueci minha senha (1.9)
// ---------------------------------------------------------------------------

/**
 * `POST /auth/recuperar-senha` — devolve **a mesma** mensagem para todo e-mail (regra 17).
 *
 * 🚨 A tela mostra a mensagem do servidor e **nada mais**: nenhum "verifique se digitou certo", nenhum
 * "não encontramos". E não há link na resposta — ele só existe no e-mail.
 */
export async function pedirRedefinicao(
  dados: DadosPedirRedefinicao,
): Promise<{ mensagem: string }> {
  const { data } = await api.post<{ mensagem: string }>('/auth/recuperar-senha', {
    email: dados.email,
  });
  return data;
}

/**
 * `GET /auth/redefinir-senha/:token` — o link ainda vale? 200 = vale; 400 = não vale (vencido, usado ou
 * inexistente, sem distinção). Não devolve o e-mail da conta, ao contrário do convite.
 */
export async function verificarTokenRedefinicao(token: string): Promise<{ valido: true }> {
  const { data } = await api.get<{ valido: true }>(`/auth/redefinir-senha/${token}`);
  return data;
}

/**
 * `POST /auth/redefinir-senha` — define a senha nova e derruba **todas** as sessões (regra 22). Não
 * devolve sessão: a pessoa entra de novo, pela tela de entrada, com a senha que acabou de escolher.
 */
export async function redefinirSenha(token: string, dados: DadosRedefinirSenha): Promise<void> {
  await api.post('/auth/redefinir-senha', { token, senha: dados.senha });
}
